import { trainingZoneColorForLabel } from '../../utils/trainingZones';
import { zoneMethodName } from './TrainingZoneEditor';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoFlashOutline, IoCreateOutline } from 'react-icons/io5';
import { NmButton } from '../NmButton';
import { CustomZoneBuilder, type ZoneRow } from './ZoneCustomization';
import { displayInputDigits, normalizeInputDigits } from '../../utils/localizedInput';
import { hasOpenPowerCeiling, powerZoneScale, powerZoneSegment, powerZonePercentRange } from '../../utils/powerZoneDisplay';
import type { ZoneMethod } from '../../services/athleteSettingsApi';
import './power-zone-editor.css';

interface Props {
  ftp: string;
  onFtpChange: (value: string) => void;
  methodId: number | '';
  onMethodChange: (value: number | '') => void;
  methods: ZoneMethod[];
  zones: ZoneRow[];
  savedFtp: number;
  busy: boolean;
  savingLabels: boolean;
  onCalculate: () => Promise<void>;
  onSaveLabels: (zones: ZoneRow[]) => Promise<void>;
  customFtp: string;
  onCustomFtpChange: (value: string) => void;
  onSaveCustom: (zones: ZoneRow[]) => Promise<void>;
}

export function PowerZoneEditor(props: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [labels, setLabels] = useState<Record<number, string>>({});
  const [labelError, setLabelError] = useState('');
  const localize = (value: string | number) => displayInputDigits(String(value), i18n.language);
  const copy = (key: string, values?: Record<string, unknown>) => t(`athleteSettings.powerEditor.${key}`, values);
  const scale = powerZoneScale(props.zones, props.savedFtp);
  const isFa = i18n.language.startsWith('fa');
  const numberInput = (value: string, update: (next: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeInputDigits(event.target.value).replace('٫', '.');
    if (/^\d*(?:\.\d*)?$/.test(next)) { update(next); setError(''); }
  };

  async function calculate() {
    if (!Number.isFinite(Number(props.ftp)) || Number(props.ftp) <= 0) { setError(copy('invalidFtp')); return; }
    if (!props.methodId) { setError(copy('chooseMethod')); return; }
    setError('');
    await props.onCalculate();
    setLabels({});
    setEditing(null);
  }

  async function saveLabels() {
    setLabelError('');
    try {
      await props.onSaveLabels(props.zones.map((zone, index) => ({ ...zone, label: labels[index] ?? zone.label })));
      setLabels({}); setEditing(null);
    } catch {
      setLabelError(copy('saveFailed'));
    }
  }

  return (
    <div className="power-zone-editor">
      <header className="power-zone-editor__heading">
        <span className="power-zone-editor__icon"><IoFlashOutline aria-hidden="true" /></span>
        <div><h2>{copy('title')}</h2></div>
      </header>

      <section className="power-zone-editor__settings" aria-label={copy('settings')}>
        <div className="power-zone-editor__field">
          <label htmlFor={`${id}-ftp`}>{copy('ftpLabel')}</label>
          <div className="power-zone-editor__ftp">
            <input id={`${id}-ftp`} type="text" inputMode="decimal" dir="ltr" autoComplete="off"
              value={localize(props.ftp)} onChange={numberInput(props.ftp, props.onFtpChange)}
              aria-describedby={error ? `${id}-error` : undefined} aria-invalid={error ? true : undefined} />
            <span>{copy('watts')}</span>
          </div>
        </div>
        <div className="power-zone-editor__field">
          <label htmlFor={`${id}-method`}>{copy('method')}</label>
          <select id={`${id}-method`} value={props.methodId} onChange={(event) => {
            props.onMethodChange(event.target.value ? Number(event.target.value) : ''); setError('');
          }}>
            <option value="">{copy('chooseMethod')}</option>
            {props.methods.map(method => <option key={method.id} value={method.id}>{zoneMethodName(method, i18n.language)}</option>)}
          </select>
        </div>
      </section>
      {error && <p id={`${id}-error`} className="power-zone-editor__error" role="alert">{error}</p>}

      <section className="power-zone-editor__results" aria-labelledby={`${id}-zones`} aria-busy={props.busy}>
        <div className="power-zone-editor__section-heading">
          <h3 id={`${id}-zones`}>{copy('zones')}</h3>
          {props.zones.length > 0 && <span>{localize(copy('count', { count: props.zones.length }))}</span>}
        </div>
        {props.zones.length === 0 ? <p className="power-zone-editor__empty">{copy('empty')}</p> : (
          <ol className="power-zone-editor__list">
            {props.zones.map((zone, index) => {
              const open = index === props.zones.length - 1 && hasOpenPowerCeiling(zone.maximum, props.savedFtp);
              const percentages = powerZonePercentRange(props.zones, index, props.savedFtp);
              const low = percentages?.low ?? null;
              const high = percentages?.high ?? null;
              const automaticLabel = /^Zone\s+\d+\s*-\s*\d+%$/i.test(normalizeInputDigits(zone.label));
              const name = labels[index] ?? (automaticLabel ? copy('zone', { number: localize(index + 1) }) : localize(zone.label).replace(/^زون(?=\s+[۰-۹0-9])/, 'ناحیه'));
              const range = open ? t('athleteSettings.powerZoneFrom', { value: localize(zone.minimum) })
                : copy('range', { minimum: localize(zone.minimum), maximum: localize(zone.maximum) });
              const percent = low === null ? '' : open ? copy('percentGreater', { value: localize(low) })
                : copy('percentRange', { minimum: localize(low), maximum: localize(high ?? 0) });
              const segment = powerZoneSegment(zone.minimum, zone.maximum, props.savedFtp, scale, open);
              return (
                <li key={index} className="power-zone-editor__zone" style={{ '--zone-start': '0%', '--zone-fill': `${segment.start + segment.width}%`, '--zone-color': trainingZoneColorForLabel(zone.label ?? '', index), '--zone-threshold': `${100 / scale * 100}%` } as React.CSSProperties}>
                  <span className="power-zone-editor__number" aria-hidden="true">{localize(index + 1)}</span>
                  <div className="power-zone-editor__zone-content">
                    <div className="power-zone-editor__zone-top">
                      {editing === index ? <input className="power-zone-editor__label-input" autoFocus aria-label={copy('editName')}
                        value={localize(labels[index] ?? zone.label)} onChange={event => setLabels(previous => ({ ...previous, [index]: event.target.value }))}
                        onBlur={() => setEditing(null)} onKeyDown={event => { if (event.key === 'Enter' || event.key === 'Escape') setEditing(null); }} />
                        : <button className="power-zone-editor__name" type="button" onClick={() => setEditing(index)} title={copy('editName')}>
                          {name}<span className="power-zone-editor__edit-icon"><IoCreateOutline aria-hidden="true" /></span>
                        </button>}
                      <span className="power-zone-editor__range" dir={isFa ? 'rtl' : 'auto'}>{range}</span>
                    </div>
                    <div className="power-zone-editor__zone-bottom">
                      <div className="power-zone-editor__plot"><div className={`power-zone-editor__track${open ? ' power-zone-editor__track--open' : ''}`} aria-hidden="true"><span /></div></div>
                      <span className="power-zone-editor__percent" dir="auto">{percent}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {labelError && <p className="power-zone-editor__error" role="alert">{labelError}</p>}
        {Object.keys(labels).length > 0 && <NmButton onClick={() => void saveLabels()} disabled={props.savingLabels}>
          {copy(props.savingLabels ? 'saving' : 'saveNames')}
        </NmButton>}
      </section>

      <NmButton variant="primary" className="power-zone-editor__calculate" onClick={() => void calculate()} disabled={props.busy}>
        <IoFlashOutline aria-hidden="true" />{copy(props.busy ? 'calculating' : 'calculate')}
      </NmButton>
      <div className="power-zone-editor__custom">
        <CustomZoneBuilder unit={copy('watts')} percentageThreshold={{ value: props.customFtp, label: copy('ftpLabel') }} thresholdFields={<div className="power-zone-editor__field">
          <label htmlFor={`${id}-custom-ftp`}>{copy('ftpLabel')}</label>
          <input id={`${id}-custom-ftp`} type="text" inputMode="decimal" dir="ltr" value={localize(props.customFtp)}
            onChange={numberInput(props.customFtp, props.onCustomFtpChange)} />
        </div>} onSave={props.onSaveCustom} />
      </div>
    </div>
  );
}
